import { css } from '@emotion/react';
import {
  AspectRatio,
  Badge,
  Box,
  Flex,
  OptimizeImage,
  Stack,
  Text,
  Title,
  Tooltip,
  UnstyledButton,
  desktopFirstMediaQuery,
} from '@inflearn/ds-react';
import { useLang } from '@inflearn/i18n-tools';
import { useTranslation } from 'next-i18next';
import { useMemo, useState } from 'react';
import { NEXT_PUBLIC_CDN_URL } from '../../../../appConfig';
import { eventNames } from '../../../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../../../utils/GTM/useTrackMixpanelEvent';
import {
  JOIN_INSTRUCTOR_CONTENT_TYPES,
  SHARE_METHOD_CONTENT_NAMES,
} from './constants/gtmEventParams';

const KnowledgeTypesSection = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const { isKo } = useLang();
  const { trackMixpanelEvent } = useTrackMixpanelEvent();
  const KNOWLEDGE_TYPES = useMemo(() => {
    if (!isKo) {
      return [
        {
          contentName: SHARE_METHOD_CONTENT_NAMES.ON,
          name: t('open.knowledge.types.course.name'),
          imageUrl: `${NEXT_PUBLIC_CDN_URL}/assets/images/header/course.png`,
          videoUrl: `${NEXT_PUBLIC_CDN_URL}/assets/images/open-knowledge/vod-tutorialx2-en.mp4`,
          tooltip: t('open.knowledge.types.course.tooltip'),
          description: t('open.knowledge.types.course.description'),
          recommendations: [
            t('open.knowledge.types.course.rec1'),
            t('open.knowledge.types.course.rec2'),
          ],
        },
      ];
    }
    return [
      {
        contentName: SHARE_METHOD_CONTENT_NAMES.ON,
        name: t('open.knowledge.types.course.name'),
        imageUrl: `${NEXT_PUBLIC_CDN_URL}/assets/images/header/course.png`,
        videoUrl: `${NEXT_PUBLIC_CDN_URL}/assets/images/open-knowledge/vod-tutorialx2.mp4`,
        tooltip: t('open.knowledge.types.course.tooltip'),
        description: t('open.knowledge.types.course.description'),
        recommendations: [
          t('open.knowledge.types.course.rec1'),
          t('open.knowledge.types.course.rec2'),
        ],
      },
      {
        contentName: SHARE_METHOD_CONTENT_NAMES.CHALLENGE,
        name: t('open.knowledge.types.challenge.name'),
        imageUrl: `${NEXT_PUBLIC_CDN_URL}/assets/images/header/challenge.png`,
        videoUrl: `${NEXT_PUBLIC_CDN_URL}/assets/images/open-knowledge/challenge-tutorialx2.mp4`,
        tooltip: t('open.knowledge.types.challenge.tooltip'),
        description: t('open.knowledge.types.challenge.description'),
        recommendations: [
          t('open.knowledge.types.challenge.rec1'),
          t('open.knowledge.types.challenge.rec2'),
        ],
      },
      {
        contentName: SHARE_METHOD_CONTENT_NAMES.MENTORING,
        name: t('open.knowledge.types.mentoring.name'),
        imageUrl: `${NEXT_PUBLIC_CDN_URL}/assets/images/header/mentoring.png`,
        videoUrl: `${NEXT_PUBLIC_CDN_URL}/assets/images/open-knowledge/mentoring-tutorialx2.mp4`,
        tooltip: t('open.knowledge.types.mentoring.tooltip'),
        description: t('open.knowledge.types.mentoring.description'),
        recommendations: [
          t('open.knowledge.types.mentoring.rec1'),
          t('open.knowledge.types.mentoring.rec2'),
        ],
      },
      {
        contentName: SHARE_METHOD_CONTENT_NAMES.CLIP,
        name: t('open.knowledge.types.clip.name'),
        imageUrl: `${NEXT_PUBLIC_CDN_URL}/assets/images/header/clip.png`,
        videoUrl: `${NEXT_PUBLIC_CDN_URL}/assets/images/open-knowledge/clip-tutorialx2.mp4`,
        tooltip: t('open.knowledge.types.clip.tooltip'),
        description: t('open.knowledge.types.clip.description'),
        recommendations: [t('open.knowledge.types.clip.rec1'), t('open.knowledge.types.clip.rec2')],
      },
    ];
  }, [isKo]);

  return (
    <Box component="section" css={styleSection}>
      <Flex direction="column" align="center" css={styleContainer}>
        <Stack spacing={40} align="center" css={styleContents}>
          <Title order={2} align="center" color="dark.9" css={styleSectionTitle}>
            {t('open.knowledge.types.title')}
          </Title>

          {/* Desktop: tab cards + shared video */}
          <Stack spacing={16} css={styleDesktopCardGroup}>
            <Flex gap={16} css={styleCardRow}>
              {KNOWLEDGE_TYPES.map((type, index) => {
                const isActive = currentIndex === index;
                return (
                  <Tooltip
                    key={type.name}
                    label={type.tooltip}
                    withArrow
                    position="top"
                    withinPortal={false}
                  >
                    <UnstyledButton
                      onClick={() => {
                        setCurrentIndex(index);
                        const type = KNOWLEDGE_TYPES[index];
                        trackMixpanelEvent(eventNames.CLICK_JOIN_INSTRUCTOR_CONTENT, {
                          content_type: JOIN_INSTRUCTOR_CONTENT_TYPES.SHARE_METHOD,
                          content_name: type.contentName,
                        });
                      }}
                      css={styleCard(isActive)}
                    >
                      <Flex align="center" gap={8}>
                        <OptimizeImage
                          width={44}
                          w={88}
                          src={type.imageUrl}
                          css={isActive ? undefined : { filter: 'grayscale(1)' }}
                        />
                        <Text color={isActive ? 'gray.9' : 'gray.7'} css={styleCardName}>
                          {type.name}
                        </Text>
                      </Flex>
                      <Text color={isActive ? 'gray.9' : 'gray.6'} css={styleCardDesc}>
                        {type.description}
                      </Text>
                      <Stack spacing={4} css={styleRecommendations}>
                        <Badge
                          variant="light"
                          color={isActive ? 'infgreen' : 'gray'}
                          size="lg"
                          radius="sm"
                          w="fit-content"
                        >
                          {t('open.knowledge.types.recommended')}
                        </Badge>
                        {type.recommendations.map((rec) => (
                          <Text key={rec} color={isActive ? 'gray.7' : 'gray.6'} css={styleRecText}>
                            {rec}
                          </Text>
                        ))}
                      </Stack>
                    </UnstyledButton>
                  </Tooltip>
                );
              })}
            </Flex>
            <AspectRatio ratio={16 / 9} css={styleImagePlaceholder}>
              {KNOWLEDGE_TYPES.map((type, index) =>
                index === currentIndex ? (
                  <video key={type.videoUrl} src={type.videoUrl} muted autoPlay loop />
                ) : null,
              )}
            </AspectRatio>
          </Stack>

          <Box css={[styleMobileScrollWrapper, !isKo && styleMobileScrollWrapperFull]}>
            <Flex gap={16} css={[styleMobileCardRow, !isKo && styleMobileCardRowFull]}>
              {KNOWLEDGE_TYPES.map((type) => (
                <Box key={type.name} css={[styleMobileCard, !isKo && styleMobileCardFull]}>
                  <Flex align="center" gap={8} css={styleMobileCardHeader}>
                    <OptimizeImage w={88} src={type.imageUrl} css={styleMobileCardIcon} />
                    <Text color="gray.9" css={styleMobileCardName}>
                      {type.name}
                    </Text>
                  </Flex>
                  <Text color="gray.9" css={styleMobileCardDesc}>
                    {type.description}
                  </Text>
                  <AspectRatio ratio={16 / 9} css={styleMobileVideoPlaceholder}>
                    <video src={type.videoUrl} muted autoPlay loop />
                  </AspectRatio>
                </Box>
              ))}
            </Flex>
          </Box>
        </Stack>
      </Flex>
    </Box>
  );
};

export default KnowledgeTypesSection;

const styleSection = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#ffffff',
  padding: '100px 0',
  width: '100%',
  [desktopFirstMediaQuery.mobile]: {
    padding: '80px 0',
  },
});

const styleContainer = css({
  maxWidth: 1200,
  width: '100%',
  padding: '0 32px',
  [desktopFirstMediaQuery.mobile]: {
    padding: '0 16px',
  },
});

const styleContents = css({
  maxWidth: 1200,
  width: '100%',
});

const styleSectionTitle = css({
  fontSize: 34,
  lineHeight: 1.3,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 24,
  },
});

// Desktop card group
const styleDesktopCardGroup = css({
  width: '100%',
  [desktopFirstMediaQuery.tablet]: {
    display: 'none',
  },
});

const styleCardRow = css({
  width: '100%',
});

const styleCard = (active: boolean) =>
  css({
    flex: '1 0 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 20,
    borderRadius: 16,
    border: active ? '2px solid #dee2e6' : '2px solid transparent',
    backgroundColor: active ? '#ffffff' : '#f1f3f5',
    minWidth: 0,
    overflow: 'hidden',
    cursor: 'pointer',
  });

const styleCardName = css({
  fontSize: 22,
  fontWeight: 700,
  lineHeight: 1.4,
});

const styleCardDesc = css({
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
});

const styleRecommendations = css({
  paddingTop: 8,
});

const styleRecText = css({
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1.5,
});

const styleImagePlaceholder = css({
  width: '100%',
  borderRadius: 16,
  overflow: 'hidden',
});

// Mobile/Tablet scrollable cards
const styleMobileScrollWrapper = css({
  display: 'none',
  width: '100%',
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
  scrollSnapType: 'x mandatory',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  scrollbarWidth: 'none',
  [desktopFirstMediaQuery.tablet]: {
    display: 'block',
  },
});

const styleMobileCardRow = css({
  width: 'max-content',
});

const styleMobileCard = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  padding: 20,
  borderRadius: 16,
  border: '2px solid #dee2e6',
  backgroundColor: '#ffffff',
  scrollSnapAlign: 'start',
  width: 630,
  flexShrink: 0,
  [desktopFirstMediaQuery.mobile]: {
    width: 320,
  },
});

const styleMobileCardHeader = css({
  paddingBottom: 4,
});

const styleMobileCardIcon = css({
  width: 44,
  height: 44,
  [desktopFirstMediaQuery.mobile]: {
    width: 32,
    height: 32,
  },
});

const styleMobileCardName = css({
  fontSize: 22,
  fontWeight: 700,
  lineHeight: 1.4,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 18,
  },
});

const styleMobileCardDesc = css({
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  flexGrow: 1,
});

const styleMobileVideoPlaceholder = css({
  width: '100%',
  borderRadius: 12,
  overflow: 'hidden',
});

const styleMobileScrollWrapperFull = css({
  overflowX: 'visible',
  scrollSnapType: 'none',
});

const styleMobileCardRowFull = css({
  [desktopFirstMediaQuery.mobile]: {
    width: '100%',
  },
});

const styleMobileCardFull = css({
  [desktopFirstMediaQuery.mobile]: {
    width: '100%',
  },
});
